import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, db } from '../../admin/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { useOutlet } from '../../context/OutletContext';

const WaiterAuthContext = createContext();

export const useWaiterAuth = () => {
  const context = useContext(WaiterAuthContext);
  if (!context) {
    throw new Error('useWaiterAuth must be used within WaiterAuthProvider');
  }
  return context;
};

export const WaiterAuthProvider = ({ children }) => {
  const { selectedOutlet, selectOutlet, outlets, getOutletById, loading: outletsLoading } = useOutlet();
  const [currentWaiter, setCurrentWaiter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [outletError, setOutletError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Check if user is a waiter in users collection
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Only allow login if user has WAITER role
            if (userData.role === 'waiter') {
              // Handle assignedOutlets - could be string or array
              let outletsArray = [];
              if (typeof userData.assignedOutlets === 'string') {
                outletsArray = [userData.assignedOutlets];
              } else if (Array.isArray(userData.assignedOutlets)) {
                outletsArray = userData.assignedOutlets;
              }

              // Handle both isActive and active field names
              const isActive = userData.isActive !== undefined ? userData.isActive : userData.active;

              const waiterData = {
                uid: user.uid,
                email: user.email,
                name: userData.name,
                role: userData.role,
                assignedOutlets: outletsArray,
                primaryOutlet: outletsArray[0] || null,
                active: isActive
              };
              setCurrentWaiter(waiterData);
            } else {
              console.warn('⚠️ User is not a waiter');
              setCurrentWaiter(null);
            }
          } else {
            setCurrentWaiter(null);
          }
        } catch (error) {
          console.error('Error fetching waiter data:', error);
          setCurrentWaiter(null);
        }
      } else {
        setCurrentWaiter(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []); // Removed dependencies to prevent infinite loop

  // Auto-select waiter's primary outlet after outlets have loaded
  useEffect(() => {
    // Only proceed if:
    // 1. Outlets have finished loading
    // 2. Waiter is logged in
    // 3. No outlet is currently selected
    // 4. Waiter has a primary outlet assigned
    if (!outletsLoading && currentWaiter && !selectedOutlet && currentWaiter.primaryOutlet && selectOutlet) {
      console.log(`👤 Auto-selecting waiter's primary outlet: ${currentWaiter.primaryOutlet}`);
      console.log(`📊 Available outlets: ${outlets.length}`);

      const outletExists = getOutletById(currentWaiter.primaryOutlet);

      if (outletExists) {
        console.log(`✅ Found outlet: ${outletExists.name}`);
        selectOutlet(currentWaiter.primaryOutlet);
        setOutletError(null);
      } else {
        console.error(`❌ Assigned outlet ${currentWaiter.primaryOutlet} not found!`);
        console.error(`📋 Available outlet IDs:`, outlets.map(o => o.id));
        setOutletError(`Your assigned outlet was not found. Please contact admin to reassign you to a valid outlet.`);
      }
    }
  }, [outletsLoading, currentWaiter, selectedOutlet, outlets, getOutletById, selectOutlet]);

  // Check if waiter has access to outlet (removed to prevent logout loop)

  const login = async (email, password) => {
    try {
      console.log('🔐 Attempting waiter login for:', email);

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('✅ Firebase Auth successful, UID:', userCredential.user.uid);

      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      console.log('📊 Checking users collection...');

      if (!userDoc.exists()) {
        console.error('❌ User document not found in users collection');
        console.error('💡 Solution: Create a document in users collection with UID:', userCredential.user.uid);
        console.error('💡 Required fields: { role: "waiter", isActive: true, assignedOutlets: [...], name: "..." }');
        await signOut(auth);
        return { success: false, error: 'User account not found in system. Contact admin to set up your waiter account.' };
      }

      const userData = userDoc.data();
      const isActive = userData.isActive !== undefined ? userData.isActive : userData.active;
      console.log('👤 User data:', {
        role: userData.role,
        isActive: isActive,
        assignedOutlets: userData.assignedOutlets
      });

      // Check if user has waiter role
      if (userData.role !== 'waiter') {
        console.error(`❌ Invalid role: "${userData.role}". Expected: "waiter"`);
        console.error('💡 Solution: Update users document to set role: "waiter"');
        await signOut(auth);
        return { success: false, error: `Access denied. This login is for waiters only (current role: ${userData.role}).` };
      }

      // Check if user is active
      if (!isActive) {
        console.error('❌ User account is deactivated');
        console.error('💡 Solution: Update users document to set isActive: true');
        await signOut(auth);
        return { success: false, error: 'Your account has been deactivated. Contact admin.' };
      }

      console.log('✅ All checks passed! Waiter login successful');
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('❌ Login error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);

      let errorMessage = 'Login failed. Please try again.';

      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'User not found';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email format';
      }

      return { success: false, error: errorMessage };
    }
  };

  const loginWithPIN = async (pin) => {
    try {
      // Query Firestore for waiter with matching PIN in users collection
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('pin', '==', pin), where('role', '==', 'waiter'));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return { success: false, error: 'Invalid PIN' };
      }

      const userData = querySnapshot.docs[0].data();
      const userId = querySnapshot.docs[0].id;

      // Check if user is active (handle both isActive and active field names)
      const isActive = userData.isActive !== undefined ? userData.isActive : userData.active;
      if (!isActive) {
        return { success: false, error: 'Your account has been deactivated. Contact admin.' };
      }

      // If user has email/password, sign in with that
      if (userData.email) {
        // Note: We can't use stored password for security reasons
        // PIN login should only work if email/password auth succeeds
        return { success: false, error: 'PIN login requires initial email/password login. Please use Email/Password tab.' };
      }

      // For now, PIN-only login is not supported
      // Waiters must login with email/password first
      return { success: false, error: 'PIN login not available. Please use Email/Password login.' };
    } catch (error) {
      console.error('Login with PIN error:', error);
      return { success: false, error: 'PIN login failed. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentWaiter(null);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  // Check if waiter has access to specific outlet
  const hasAccessToOutlet = (waiter, outletId) => {
    if (!waiter || !outletId) return false;

    // Managers can access all outlets
    if (waiter.role === 'manager') {
      return true;
    }

    // Check if outlet is in waiter's assigned outlets
    return waiter.assignedOutlets && waiter.assignedOutlets.includes(outletId);
  };

  // Get outlets waiter has access to
  const getAccessibleOutlets = (waiter) => {
    if (!waiter) return [];
    return waiter.assignedOutlets || [];
  };

  const value = {
    currentWaiter,
    login,
    loginWithPIN,
    logout,
    loading,
    hasAccessToOutlet,
    getAccessibleOutlets,
    outletError
  };

  return (
    <WaiterAuthContext.Provider value={value}>
      {children}
    </WaiterAuthContext.Provider>
  );
};
